/**
 * Datasets tab — M6 §5 "Datasets", §4 management.
 *
 * Everything already in the catalogue, unpublished rows included, with the four acts a
 * manager has over a published dataset: edit its metadata, add a version, hide it from the
 * public catalogue, delete it.
 *
 * **The list is `include_hidden`, and hidden rows are marked rather than dropped.** A
 * manager who has just unpublished something has to be able to find it again — a table that
 * quietly stopped listing it would read as "delete", which is the one act on this page that
 * cannot be undone.
 *
 * **Filtering is client-side over one payload.** The catalogue is a few hundred rows at most
 * and §F8 already narrows the same way (`catalogue.applyFilters`), so search and the two
 * selects never touch the network: a manager hunting for a title types and sees the table
 * move, rather than typing and waiting.
 *
 * **Delete asks for the id, in words.** The dialog is a typed confirmation (the id, exactly)
 * because the row's own title is not unique and a "Are you sure?" button is one mis-click.
 * The server refuses seeded and harvested datasets with a 409 carrying its own reason, and
 * that reason is printed as it came: only the server knows *why* a given id is protected.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Seg } from '../../components/Seg';
import {
  ApiError,
  deleteDataset,
  fetchDatasetsAll,
  queryKeys,
  setDatasetVisibility,
  type ManagedDatasetRow,
} from '../../lib/api';
import { NO_VALUE } from '../../lib/format';
import { toast } from '../../lib/toast';
import { EditDatasetModal } from './EditDatasetModal';
import { relativeTime } from './manager';

/** Everything inside the dialog a Tab can reach, in document order (the record's list). */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** §5 "type/visibility filters". `all` is a client-side word — nothing is sent on the wire. */
const DS_TYPE_FILTERS = ['all', 'raster', 'vector', 'tabular'] as const;
const DS_VISIBILITY_FILTERS = ['all', 'visible', 'hidden'] as const;

type DsTypeFilter = (typeof DS_TYPE_FILTERS)[number];
type DsVisibilityFilter = (typeof DS_VISIBILITY_FILTERS)[number];

interface DatasetFilterState {
  q: string;
  type: DsTypeFilter;
  visibility: DsVisibilityFilter;
}

/**
 * Search over the three fields a manager would search by — title, id and organisation — plus
 * the two selects. Case-insensitive and accent-naive, which matches §F8's own search box.
 */
function filterDatasets(
  rows: readonly ManagedDatasetRow[],
  { q, type, visibility }: DatasetFilterState,
): ManagedDatasetRow[] {
  const needle = q.trim().toLowerCase();
  return rows.filter((row) => {
    if (type !== 'all' && row.data_type !== type) return false;
    if (visibility === 'visible' && !row.website_visible) return false;
    if (visibility === 'hidden' && row.website_visible) return false;
    if (!needle) return true;
    return `${row.title} ${row.id} ${row.source_org}`.toLowerCase().includes(needle);
  });
}

export interface DatasetsTabProps {
  /** "New version" — the page switches to the wizard with this dataset preset (§4). */
  onNewVersion: (datasetId: string) => void;
}

export function DatasetsTab({ onNewVersion }: DatasetsTabProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [type, setType] = useState<DsTypeFilter>('all');
  const [visibility, setVisibility] = useState<DsVisibilityFilter>('all');
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isError, isPending } = useQuery({
    queryKey: queryKeys.datasetsAll(),
    queryFn: fetchDatasetsAll,
  });

  const rows = useMemo(() => data?.results ?? [], [data]);
  const shown = useMemo(
    () => filterDatasets(rows, { q, type, visibility }),
    [rows, q, type, visibility],
  );
  const editRow = editing === null ? null : (rows.find((r) => r.id === editing) ?? null);
  const deleteRow = deleting === null ? null : (rows.find((r) => r.id === deleting) ?? null);

  const refetchAll = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['datasetsAll'] });
    // The public catalogue and `/layers` both change when a dataset is hidden or removed, so
    // they are stale here too — not only the manager's own list.
    void qc.invalidateQueries({ queryKey: ['datasets'] });
    // `['dataset', id]` is not a prefix of `['datasets']`: the record the edit dialog reads
    // is a separate cache entry, and leaving it alone is how a second Edit shows the values
    // that were just replaced.
    void qc.invalidateQueries({ queryKey: ['dataset'] });
    void qc.invalidateQueries({ queryKey: ['layers'] });
    // A delete takes the uploads that published the dataset with it (§4), and an edit changes
    // the title the review queue prints for a new version of it — so the queue is stale too.
    void qc.invalidateQueries({ queryKey: ['uploads'] });
  }, [qc]);

  const toggleVisibility = async (row: ManagedDatasetRow) => {
    setBusy(row.id);
    try {
      await setDatasetVisibility(row.id, !row.website_visible);
      await refetchAll();
      toast(
        t(row.website_visible ? 'manager.ds.unpublished' : 'manager.ds.republished', {
          title: row.title,
        }),
      );
    } catch (err) {
      toast(t('manager.ds.visibilityFailed', { message: messageOf(err) }));
    } finally {
      setBusy(null);
    }
  };

  const typeOptions = DS_TYPE_FILTERS.map((value) => ({
    value,
    label: t(`manager.ds.type.${value}`),
    testId: `mgr-ds-type-${value}`,
  }));
  const visOptions = DS_VISIBILITY_FILTERS.map((value) => ({
    value,
    label: t(`manager.ds.vis.${value}`),
    testId: `mgr-ds-vis-${value}`,
  }));

  return (
    <div className="card mgr-ds" data-testid="mgr-datasets">
      <div className="card-head">
        <div className="card-head-text">
          <h3>{t('manager.ds.title')}</h3>
          <p className="sub">{t('manager.ds.sub')}</p>
        </div>
      </div>

      <div className="mgr-ds-controls">
        <label className="mgr-ds-search" htmlFor="mgrDsSearch">
          <span className="sr-only">{t('manager.ds.searchLabel')}</span>
          <input
            id="mgrDsSearch"
            data-testid="mgr-ds-search"
            className="mgr-input"
            type="search"
            placeholder={t('manager.ds.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <Seg
          options={typeOptions}
          value={type}
          onChange={setType}
          ariaLabel={t('manager.ds.typeLabel')}
        />
        <Seg
          options={visOptions}
          value={visibility}
          onChange={setVisibility}
          ariaLabel={t('manager.ds.visLabel')}
        />
      </div>

      {isError ? (
        <p className="rows-note" data-testid="mgr-ds-error">
          {t('manager.ds.error')}
        </p>
      ) : isPending ? (
        <p className="rows-note">{t('manager.ds.loading')}</p>
      ) : shown.length === 0 ? (
        <p className="rows-note" data-testid="mgr-ds-empty">
          {rows.length === 0 ? t('manager.ds.empty') : t('manager.ds.noMatch')}
        </p>
      ) : (
        <div className="rows-wrap">
          <table className="mini rows-table mgr-ds-table">
            <thead>
              <tr>
                <th scope="col">{t('manager.ds.col.title')}</th>
                <th scope="col">{t('manager.ds.col.type')}</th>
                <th scope="col">{t('manager.ds.col.versions')}</th>
                <th scope="col">{t('manager.ds.col.visibility')}</th>
                <th scope="col">{t('manager.ds.col.source')}</th>
                <th scope="col">{t('manager.ds.col.updated')}</th>
                <th scope="col">{t('manager.ds.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <DatasetRow
                  key={row.id}
                  row={row}
                  lang={i18n.language}
                  busy={busy === row.id}
                  onEdit={() => setEditing(row.id)}
                  onNewVersion={() => onNewVersion(row.id)}
                  onToggle={() => void toggleVisibility(row)}
                  onDelete={() => setDeleting(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRow ? (
        <EditDatasetModal
          row={editRow}
          onClose={() => setEditing(null)}
          onSaved={() => void refetchAll()}
        />
      ) : null}

      {deleteRow ? (
        <DeleteDialog
          row={deleteRow}
          onClose={() => setDeleting(null)}
          onDeleted={() => void refetchAll()}
        />
      ) : null}
    </div>
  );
}

interface DatasetRowProps {
  row: ManagedDatasetRow;
  lang: string;
  busy: boolean;
  onEdit: () => void;
  onNewVersion: () => void;
  onToggle: () => void;
  onDelete: () => void;
}

function DatasetRow({ row, lang, busy, onEdit, onNewVersion, onToggle, onDelete }: DatasetRowProps) {
  const { t } = useTranslation();
  const when = relativeTime(row.updated_at, lang);
  // A count the server did not send is an em dash, not a `0`: "no versions" is a different
  // claim from "this server does not tell me", and a published dataset always has one.
  const versions = row.versions_count ?? null;

  return (
    <tr className="mgr-ds-row" data-testid={`mgr-ds-row-${row.id}`}>
      <td className="mgr-ds-title">
        <span>{row.title}</span>
        <em>{row.id}</em>
      </td>
      <td>
        <span className={`chip mgr-ds-type ${row.data_type}`}>
          {t(`catalogue.type.${row.data_type}`)}
        </span>
      </td>
      <td className="mgr-ds-versions">
        <span>{versions === null ? NO_VALUE : versions}</span>
        {row.latest_version ? <em>{row.latest_version.version}</em> : null}
      </td>
      <td>
        <span
          className={`pill mgr-pill ${row.website_visible ? 'approved' : 'rejected'}`}
          data-testid={`mgr-ds-visible-${row.id}`}
        >
          {t(row.website_visible ? 'manager.ds.visible' : 'manager.ds.hidden')}
        </span>
      </td>
      <td>{row.source_org || NO_VALUE}</td>
      <td className="mgr-ds-when">{when ?? NO_VALUE}</td>
      <td className="mgr-ds-actions">
        <button type="button" className="btn ghost sm" data-testid={`mgr-ds-edit-${row.id}`} onClick={onEdit}>
          {t('manager.ds.edit')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          data-testid={`mgr-ds-version-${row.id}`}
          onClick={onNewVersion}
        >
          {t('manager.ds.newVersion')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          data-testid={`mgr-ds-visibility-${row.id}`}
          disabled={busy}
          onClick={onToggle}
        >
          {t(row.website_visible ? 'manager.ds.unpublish' : 'manager.ds.republish')}
        </button>
        {/* A dataset the server has said it will not delete keeps its button: the refusal is
            the server's to make and to explain, and a button that is simply gone tells the
            reader nothing. The title says so before they click. */}
        <button
          type="button"
          className="btn ghost sm mgr-danger"
          data-testid={`mgr-ds-delete-${row.id}`}
          title={row.deletable === false ? t('manager.ds.notDeletable') : undefined}
          onClick={onDelete}
        >
          {t('manager.ds.delete')}
        </button>
      </td>
    </tr>
  );
}

interface DeleteDialogProps {
  row: ManagedDatasetRow;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * The typed confirmation. The danger button turns on only for the id spelled exactly as the
 * row prints it, and a 409 leaves the dialog open with the server's reason under the box —
 * the reader's next move is "close", not "type it again harder".
 *
 * Focus, Tab-trapping and Escape are `EditDatasetModal`'s, which are `DatasetRecord`'s: this
 * is the dialog that deletes data, so a Tab that wanders out of it and back onto the row's
 * own Delete button is exactly the confusion it must not create.
 */
function DeleteDialog({ row, onClose, onDeleted }: DeleteDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    inputRef.current?.focus();
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteDataset(row.id, typed);
      onDeleted();
      toast(t('manager.ds.deleted', { title: row.title }));
      onClose();
    } catch (err) {
      setError(reasonOf(err) ?? t('manager.ds.deleteFailed'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-back show" onKeyDown={trapTab}>
      <div
        className="modal mgr-delete"
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mgrDeleteTitle"
        data-testid="mgr-delete"
      >
        <header>
          <h3 id="mgrDeleteTitle">{t('manager.ds.deleteTitle', { title: row.title })}</h3>
        </header>
        <div className="mbody">
          <p className="mgr-hint">{t('manager.ds.deleteBody')}</p>
          <label className="mgr-field" htmlFor="mgrDeleteConfirm">
            <span className="mgr-label">{t('manager.ds.deleteConfirm', { id: row.id })}</span>
            <input
              id="mgrDeleteConfirm"
              data-testid="mgr-delete-input"
              className="mgr-input"
              ref={inputRef}
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
          {error ? (
            <p className="mgr-delete-error" data-testid="mgr-delete-error">
              {error}
            </p>
          ) : null}
        </div>
        <footer>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('manager.ds.cancel')}
          </button>
          <button
            type="button"
            className="btn primary mgr-delete-go"
            data-testid="mgr-delete-go"
            disabled={typed !== row.id || busy}
            onClick={() => void confirm()}
          >
            {busy ? t('manager.ds.deleting') : t('manager.ds.deleteGo')}
          </button>
        </footer>
      </div>
    </div>
  );
}

const messageOf = (err: unknown): string =>
  err instanceof ApiError || err instanceof Error ? err.message : '';

/**
 * The server's own reason for refusing a delete (§4: seeds, the registry, harvested ids).
 *
 * The live refusal is
 * `{"error": {"code": "not_deletable", "message": "'soc' cannot be deleted: published by the
 * 'fixture_cogs' pipeline step."}, "code": "not_deletable", "reason": "…"}` — the §D envelope
 * with the reason repeated at the top level. `sendJson` parses the envelope, so the reason
 * arrives as `ApiError.message`, and that is what this reads. `details.reason` is honoured
 * first for a server that ever puts it there instead.
 *
 * Only a `not_deletable` yields a reason: every other failure gets the generic string,
 * because "Internal Server Error" is not something a reader can act on.
 */
function reasonOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'not_deletable') return null;
  const details = err.details;
  if (details && typeof details === 'object' && 'reason' in details) {
    const reason = (details as { reason?: unknown }).reason;
    if (typeof reason === 'string' && reason.trim()) return reason;
  }
  return err.message?.trim() ? err.message : null;
}

export default DatasetsTab;
